import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ChatBubble, Visibility } from '@mui/icons-material'
import { Box, Button, Card, CardContent, Chip, Divider, Grid, Typography } from '@mui/material'

import { Analyst } from '../../../../components/Analyst/Analyst'
import { AnimatedImage } from '../../../../components/AnimatedImage/AnimatedImage'
import { HtmlContentParser } from '../../../../components/HtmlContentParser/HtmlContentParser'
import { liveEventReviewModalParserOptions } from '../../../../components/LiveEventModalLink/LiveEventModalLink'
import { TranslationToggle } from '../../../../components/TranslationToggle/TranslationToggle'
import { formatEventInstanceDuration } from '../../../../helpers/date'
import { useContentTranslation } from '../../../../hooks/translationHooks'
import languageService from '../../../../services/LanguageService'
import { useInternalAccessCheck, useLiveEventsTrackerAccessCheck } from '../../../account/hooks/roleHooks'
import { useCurrentUserLanguage } from '../../../account/hooks/useCurrentUserLanguage'
import { GameCardContent } from '../../../game/components/GameCard/GameCard'
import { buildBlueprintFamilyPath, buildEventCategoryPath, buildEventTypePath } from '../../../market-analytics/components/helpers/catalogueUtils'
import { liveEventPriceRange } from '../../hooks/liveEventPriceRange'
import { useLiveEventBlueprintName } from '../../hooks/liveEventsBlueprints'
import { useBlueprintFamilyForEvent } from '../../hooks/useBlueprintFamilyForEvent'
import { useDateTimeFormatter } from '../../hooks/useDateTimeFormatter'
import { EventDialogTab } from '../../hooks/useEventDialogTabs'
import { useLiveEventTagGroupWithEventTypeId } from '../../hooks/useLiveEventTagGroups'
import { LiveEventFeedItem, LiveEventFeedItemType } from '../../types/LiveEventFeedItem'
import CurrencyDialog from '../CurrencyDialog/CurrencyDialog'
import { EventHighlightButton } from '../EventHighlightButton/EventHighlightButton'
import { HierarchyCell, LiveEventHierarchyBand } from '../LiveEventHierarchyBand/LiveEventHierarchyBand'
import { LiveEventMotivationsSection } from '../LiveEventMotivationsSection/LiveEventMotivationsSection'
import LiveEventPricePoints from '../LiveEventPricePoints/LiveEventPricePoints'
import { CurrencyButton } from '../LiveEventsEventDialog/DescriptionTab/DescriptionTab'
import { LiveEventsNotPublishedBadge } from '../LiveEventsNotPublishedBadge/LiveEventsNotPublishedBadge'
import LiveEventsSecondaryTaxonomyTagList from '../LiveEventsSecondaryTaxonomyTagList/LiveEventsSecondaryTaxonomyTagList'
import './LiveEventFeedItemCard.scss'

interface Props {
  feedItem: LiveEventFeedItem
  onOpenFeedItemClick: (feedItem: LiveEventFeedItem, tab?: EventDialogTab) => void
}

const dateFormat: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  year: 'numeric',
  month: 'short',
  day: 'numeric',
}

const LiveEventsFeedItemCard: React.FC<Props> = ({ feedItem, onOpenFeedItemClick }) => {
  const { t } = useTranslation()
  const userLanguage = useCurrentUserLanguage()
  const dateFormatter = useDateTimeFormatter(dateFormat)
  const [currenciesDialogOpen, setCurrenciesDialogOpen] = useState(false)
  const { isTranslating, translatedContent, translationError, handleTranslateClick, displayContent } = useContentTranslation()
  const isInternalUser = useInternalAccessCheck()
  const hasLiveEventsTrackerAccess = useLiveEventsTrackerAccessCheck()
  const showWipMessage = feedItem.trackingEvent?.active === false && !isInternalUser

  const tagName = useMemo(() => {
    let tagName: string = ''

    if (feedItem.type === LiveEventFeedItemType.LiveEvent && feedItem.trackingEvent?.typeId) {
      tagName = languageService.getTranslation('tags', feedItem.trackingEvent?.typeId)
    }

    return tagName
  }, [feedItem])

  const tagGroup = useLiveEventTagGroupWithEventTypeId(feedItem.trackingEvent?.typeId || '')
  const gameLevelBlueprintId = feedItem.trackingEvent?.blueprintEvolutionData?.blueprintId
  // Same resolution as the event dialog's trail: event → game-level blueprint →
  // family. Silent (no cell) whenever the family isn't known or not entitled,
  // except for a blueprint no analyst has mapped to a family yet, which gets a
  // placeholder cell explaining the gap.
  const {
    familyId,
    familyName,
    canLink: canLinkFamily,
    isUncategorized,
  } = useBlueprintFamilyForEvent({
    trackedGame: feedItem.trackedGame,
    gameLevelBlueprintId,
  })
  const blueprintName = useLiveEventBlueprintName({ blueprintId: gameLevelBlueprintId, trackedGame: feedItem.trackedGame })

  const isLiveEvent = feedItem.type === LiveEventFeedItemType.LiveEvent
  // The ribbon above the card already carries the first-appearance / mechanic-change
  // wording, so the chip only says "New" and is independent of a mechanic change.
  const isNewFirstInstance = isLiveEvent && !!feedItem.new && !!feedItem.firstInstance

  // Not memoised: the modal opener is a fresh closure over the current route on
  // every render, and four small objects are cheaper than a stale one.
  const hierarchyCells: HierarchyCell[] = []
  if (isLiveEvent && feedItem.trackingEvent) {
    const { typeId } = feedItem.trackingEvent
    // Category, type, and family have Live Event Analytics pages. Blueprint is
    // this game's own format, so it opens this event in the dialog instead.
    if (tagGroup?.name) {
      hierarchyCells.push({
        key: 'category',
        label: t('common:category'),
        value: tagGroup.name,
        to: hasLiveEventsTrackerAccess && tagGroup.id ? buildEventCategoryPath(tagGroup.id) : undefined,
        openInNewWindow: true,
        accentColor: tagGroup.colorHex,
      })
    }
    if (tagName) {
      hierarchyCells.push({
        key: 'type',
        label: t('live-events:event_type_label'),
        value: tagName,
        to: hasLiveEventsTrackerAccess && typeId ? buildEventTypePath(typeId) : undefined,
        openInNewWindow: true,
      })
    }
    if (familyName) {
      hierarchyCells.push({
        key: 'blueprintFamily',
        label: t('live-events:market_level_blueprint'),
        value: familyName,
        to: canLinkFamily && familyId ? buildBlueprintFamilyPath(familyId) : undefined,
        openInNewWindow: true,
      })
    } else if (isUncategorized) {
      hierarchyCells.push({
        key: 'blueprintFamily',
        label: t('live-events:market_level_blueprint'),
        value: t('market-analytics:granularity_blueprint'),
        isPlaceholder: true,
        note: t('live-events:event_blueprint_not_categorized'),
      })
    }
    if (blueprintName) {
      hierarchyCells.push({
        key: 'blueprint',
        label: t('live-events:blueprint'),
        value: blueprintName,
        onClick: () => onOpenFeedItemClick(feedItem, EventDialogTab.LiveEventEvolution),
      })
    }
  }

  const priceRange = liveEventPriceRange(feedItem.trackingEvent?.pricePoints, feedItem.trackedGame?.game.currencies)

  const feedItemTitle = useMemo(() => {
    if (feedItem.type === LiveEventFeedItemType.LiveEvent && feedItem.trackingEvent) {
      return feedItem.trackingEvent.name
    }

    if (feedItem.type === LiveEventFeedItemType.AnalystNote && feedItem.analystNote) {
      return feedItem.analystNote.comment.content.title[userLanguage]
        ? feedItem.analystNote.comment.content.title[userLanguage]
        : feedItem.analystNote.comment.content.title['en']
    }
  }, [feedItem, userLanguage])

  const durationText = useMemo(() => {
    return formatEventInstanceDuration(feedItem.start, feedItem.end)
  }, [feedItem])

  const screenshotURL = useMemo(() => {
    return feedItem.type === LiveEventFeedItemType.LiveEvent && feedItem.trackingEvent?.screenshotUrl ? feedItem.trackingEvent?.screenshotUrl : undefined
  }, [feedItem])

  const descriptionRawHtml = useMemo(() => {
    switch (feedItem.type) {
      case LiveEventFeedItemType.LiveEvent:
        return feedItem.trackingEvent?.comment?.content?.comment?.[userLanguage]
          ? feedItem.trackingEvent?.comment?.content?.comment?.[userLanguage]
          : feedItem.trackingEvent?.comment?.content?.comment?.['en']

      case LiveEventFeedItemType.AnalystNote:
        return feedItem.analystNote?.comment?.content?.comment?.[userLanguage]
          ? feedItem.analystNote?.comment?.content?.comment?.[userLanguage]
          : feedItem.analystNote?.comment?.content?.comment?.['en']
    }
  }, [feedItem, userLanguage])

  const gameId = useMemo(() => {
    switch (feedItem.type) {
      case LiveEventFeedItemType.LiveEvent:
        return feedItem.trackingEvent ? feedItem.trackingEvent?.gameId : ''

      case LiveEventFeedItemType.AnalystNote:
        return feedItem.analystNote?.gameId ? feedItem.analystNote?.gameId : ''
    }
  }, [feedItem])

  return (
    <div className="LiveEventFeedItemCard">
      <Box mb={2}>
        <Card className="LiveEventFeedItemCard__CardComponent">
          {feedItem.type === LiveEventFeedItemType.LiveEvent && feedItem.new && feedItem.firstInstance && !feedItem.mechanicChange && (
            <div className="LiveEventFeedItemCard__NewItemHeader" style={{ backgroundColor: tagGroup.colorHex }}>
              <strong>{t('live-events:event_type_first_appearance')}</strong>
            </div>
          )}
          {feedItem.type === LiveEventFeedItemType.LiveEvent && feedItem.mechanicChange && feedItem.firstInstance && (
            <div className="LiveEventFeedItemCard__NewItemHeader" style={{ backgroundColor: tagGroup.colorHex }}>
              <strong>{t('live-events:event_mechanic_change')}</strong>
            </div>
          )}

          <CardContent>
            <Grid container alignItems="center" spacing={2}>
              {feedItem.trackedGame && (
                <Grid item xs={3} position="relative">
                  <GameCardContent game={feedItem.trackedGame.game} displayGameType discontinued={feedItem.trackedGame.discontinued} />
                  {!feedItem.trackedGame.published && <LiveEventsNotPublishedBadge />}
                </Grid>
              )}
              <Grid item xs sx={{ minWidth: 0, textAlign: 'center' }}>
                {feedItem.type === LiveEventFeedItemType.AnalystNote && (
                  <>
                    <Typography variant="h3">{feedItemTitle}</Typography>
                    {!feedItem.analystNote?.active && (
                      <Chip label={t('live-events:event_inactive')} color="warning" size="small" sx={{ verticalAlign: 'middle', mb: '3px', mt: 1 }} />
                    )}
                  </>
                )}

                {isLiveEvent && feedItem.trackingEvent && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}>
                      {isNewFirstInstance && (
                        <Chip label={t('common:new')} size="small" sx={{ m: 0, bgcolor: tagGroup?.colorHex, color: 'common.white', borderRadius: '4px' }} />
                      )}
                      <Typography variant="h2">{feedItem.trackingEvent.name}</Typography>
                      {feedItem.trackingEvent.active === false && <Chip label={t('live-events:event_inactive')} color="warning" size="small" sx={{ m: 0 }} />}
                    </Box>
                    {/* One line when there is room, as in the design; the pair wraps rather than truncating. */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', columnGap: 3, mt: 0.5 }}>
                      <Typography className="light-text-color" variant="body2">
                        {t('live-events:duration_start')}: {dateFormatter.format(feedItem.start)}
                      </Typography>
                      <Typography className="light-text-color" variant="body2">
                        {t('live-events:duration')}: {durationText}
                      </Typography>
                    </Box>
                  </>
                )}
              </Grid>
              {/* Balances the game column so the headline centres on the card, not on the space left over beside the game. */}
              {feedItem.trackedGame && <Grid item xs={3} />}
            </Grid>
          </CardContent>

          <LiveEventHierarchyBand cells={hierarchyCells} />

          {/* The band's bottom border is the seam when it renders; doubling it with a divider read as a heavier rule. */}
          {hierarchyCells.length === 0 && <Divider />}

          <CardContent>
            <Box mb={2}>
              <Grid container spacing={4} wrap="wrap">
                <Grid className="LiveEventFeedItemCard__HTMLContent" item xs={12} md={screenshotURL ? 6 : 12}>
                  {isLiveEvent && (
                    <Box mb={2}>
                      {priceRange && (
                        <Typography className="light-text-color" variant="body1">
                          {priceRange.minPricePointValue === priceRange.maxPricePointValue
                            ? t('live-events:price_range') + `: $${priceRange.maxPricePointValue}`
                            : t('live-events:price_range') + `: $${priceRange.minPricePointValue} - $${priceRange.maxPricePointValue}`}
                        </Typography>
                      )}
                      {feedItem.trackingEvent?.tags && feedItem.trackingEvent?.tags.length > 0 && (
                        <Box paddingTop={2}>
                          <Divider sx={{ marginBottom: '10px' }}>{t('live-events:live_events_taxonomy')}</Divider>
                          <LiveEventsSecondaryTaxonomyTagList tags={feedItem.trackingEvent?.tags} justifyContent="flex-start" />
                        </Box>
                      )}
                    </Box>
                  )}

                  {feedItem.trackingEvent?.motivations && (
                    <Box mb={2}>
                      <LiveEventMotivationsSection
                        motivations={feedItem.trackingEvent?.motivations.motivations}
                        archetypes={feedItem.trackingEvent?.motivations.archetypes}
                        alignLeft
                      />
                      <Divider />
                    </Box>
                  )}

                  {!feedItem.trackingEvent?.motivations && !!feedItem.trackingEvent?.tags && (
                    <Box paddingBottom={2}>
                      <Divider />
                    </Box>
                  )}

                  {feedItem.analyst && (
                    <Box mb={2}>
                      <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                        <Grid item>
                          <Analyst picture={feedItem.analyst.picture} name={feedItem.analyst.name} title={feedItem.analyst.title} />
                        </Grid>
                        {!showWipMessage && descriptionRawHtml && userLanguage !== 'en' && (
                          <Grid item>
                            <TranslationToggle
                              isTranslating={isTranslating}
                              hasTranslation={!!translatedContent}
                              error={translationError}
                              onToggle={() => handleTranslateClick(descriptionRawHtml, userLanguage)}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  )}
                  {gameId &&
                    (showWipMessage ? (
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        {t('live-events:event_wip_tooltip')}
                      </Typography>
                    ) : (
                      <HtmlContentParser rawHtml={displayContent(descriptionRawHtml || '')} parserOptions={liveEventReviewModalParserOptions(gameId || '')} />
                    ))}
                  {feedItem.trackedGame && feedItem.trackingEvent?.pricePoints && feedItem.trackingEvent?.pricePoints.length > 0 && (
                    <>
                      <Divider sx={{ mt: 3.5, mb: 2 }}>{t('live-events:price_points')}</Divider>

                      <LiveEventPricePoints pricePoints={feedItem.trackingEvent.pricePoints} currencies={feedItem.trackedGame.game.currencies} />

                      {feedItem.trackedGame?.game?.currencies && feedItem.trackedGame.game.currencies.length > 0 && (
                        <CurrencyButton currencies={feedItem.trackedGame?.game?.currencies} onOpenDialog={() => setCurrenciesDialogOpen(true)} t={t} />
                      )}
                    </>
                  )}
                </Grid>
                <Box paddingBottom={2}>
                  <Divider />
                </Box>

                {screenshotURL && (
                  <Grid item alignSelf="top" xs={12} md={6} textAlign="center" justifyItems="center">
                    <Box className="LiveEventFeedItemCard__ItemScreenshot">
                      <AnimatedImage alt="Screenshot" url={screenshotURL} onClick={() => onOpenFeedItemClick(feedItem)} />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>

            <Divider />

            <Box mt={2}>
              <Grid container spacing={2}>
                <Grid item>
                  {feedItem.type === LiveEventFeedItemType.LiveEvent && (
                    <Button onClick={() => onOpenFeedItemClick(feedItem)} variant="contained" color="primary" startIcon={<Visibility />} fullWidth>
                      {t('live-events:game_alert_action_open_event_description')}
                    </Button>
                  )}

                  {feedItem.type === LiveEventFeedItemType.AnalystNote && (
                    <Button onClick={() => onOpenFeedItemClick(feedItem)} variant="contained" color="success" startIcon={<ChatBubble />} fullWidth>
                      {t('live-events:game_alert_action_open_comment_notes')}
                    </Button>
                  )}
                </Grid>

                <Grid item>
                  {feedItem.type === LiveEventFeedItemType.LiveEvent && feedItem.trackingEvent && (
                    <EventHighlightButton
                      eventId={feedItem.trackingEvent.eventId}
                      trackedGameId={feedItem.trackedGame?.game.id}
                      eventStartTime={feedItem.start}
                      eventEndTime={feedItem.end}
                      eventType={feedItem.trackingEvent?.typeId}
                    />
                  )}
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Box>
      <CurrencyDialog open={currenciesDialogOpen} onClose={() => setCurrenciesDialogOpen(false)} currencies={feedItem.trackedGame?.game?.currencies} />
    </div>
  )
}

export default LiveEventsFeedItemCard
